// src/utils/retourAbsence.ts — SCE Issues
// Le récap « retour de vacances » (28/10, piste A de la maquette
// https://claude.ai/artifact/TcsuwBJoSyyA3PjMGSUheU) : quelqu'un qui revient
// après plusieurs jours sans passer sur l'accueil voit l'essentiel en quelques
// cartes — l'absence, les déclarations, qui a pris le relais, ce qui l'attend,
// ses suivis, les nouveautés de l'appli, par quoi commencer. Pur, sans réseau.
//
// ─── Quand ───
//
// Au moins SEUIL_ABSENCE_JOURS entre la visite précédente de l'accueil et
// celle-ci. La visite précédente est réécrite à CHAQUE passage : le retour est
// donc noté UNE fois, dans le bloc `retourAbsence` du JSON Prefs, à côté de la
// visite (même écriture). Les passages suivants le relisent tel quel : la
// période d'absence ne bouge plus pendant les DUREE_RECAP_JOURS où le récap
// reste disponible (menu du compte), ou jusqu'à ce que tout soit fait.
//
// ─── Quoi ───
//
// Le passé (déclarations, relais, suivis) se lit sur la période figée ; « ce
// qui vous attend » et « par quoi commencer » sur l'état d'aujourd'hui. Une
// étape proposée puis réglée (plus rien à qualifier) passe d'elle-même à
// « fait » : `proposees` se souvient qu'elle a été proposée.

import {
  BADGES_CLOTURE,
  estActif,
  type BadgeCloture,
  type Commentaire,
  type Issue,
  type Metier,
} from '@/types/issue.types'
import type { Jour } from '@/types/fnct.types'
import { METIERS_ORDRE } from '@/config/metiers.config'
import { dansPerimetre, type ClePerimetre } from '@/config/sujets.config'
import type { Nouveaute, PublicNouveaute } from '@/config/nouveautes.config'
import { filesCoordinateur, JOURS_SANS_ACTIVITE, SEUIL_ABSENCE_JOURS } from '@/utils/accueil'
import { lireEvenementEnvoi } from '@/utils/envoiBE'
import { cleNom, jourParis, memePersonne } from '@/utils/fnct'
import { decalerJours, INITIALES_JOURS, rangDansSemaine } from '@/utils/calendrier'
import type { MesActions, MonAction } from '@/utils/mesFnct'
import { lienFnct, lienProbleme } from '@/utils/liensFiches'
import { prenom } from '@/utils/noms'
import { natureAutomatique } from '@/utils/rattachementPhotos'

export { SEUIL_ABSENCE_JOURS }
export const DUREE_RECAP_JOURS = 3
/** Au-delà, « par quoi commencer » ne commence plus rien. */
const MAX_ETAPES = 4
const MAX_NOUVEAUTES = 6
/** Fenêtre de comparaison pour « d'habitude ». */
const JOURS_HABITUDE = 84
/** Calendrier de la première carte : au-delà, la phrase seule. */
const MAX_JOURS_CALENDRIER = 42
const JOUR_MS = 24 * 3600 * 1000

function ms(iso: string | undefined | null): number {
  const t = iso ? Date.parse(iso) : Number.NaN
  return Number.isNaN(t) ? 0 : t
}

// ─── Le bloc retenu dans Prefs ───

export const CLE_RETOUR_ABSENCE = 'retourAbsence'

export interface BlocRetourAbsence {
  version: 1
  /** Visite précédente de l'accueil : début de l'absence (ISO). */
  depuis: string
  /** Passage qui a vu le retour (ISO). */
  retour: string
  /** Le récap n'est plus proposé après (ISO). */
  jusquAu: string
  /** Ouvert d'office une fois : il ne se rouvre plus seul. */
  vu: boolean
  /** Étapes cochées par la personne. */
  faits: string[]
  /** Étapes déjà proposées : réglées depuis, elles restent, cochées. */
  proposees: string[]
}

const chaines = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])

/** Lecture tolérante : null si le bloc manque ou ne se lit pas. */
export function lireBlocRetour(v: unknown): BlocRetourAbsence | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  const o = v as Record<string, unknown>
  const depuis = typeof o['depuis'] === 'string' ? o['depuis'] : ''
  const retour = typeof o['retour'] === 'string' ? o['retour'] : ''
  const jusquAu = typeof o['jusquAu'] === 'string' ? o['jusquAu'] : ''
  if (!ms(depuis) || !ms(retour) || !ms(jusquAu)) return null
  return { version: 1, depuis, retour, jusquAu, vu: o['vu'] === true, faits: chaines(o['faits']), proposees: chaines(o['proposees']) }
}

/** Assez longtemps sans passer sur l'accueil pour parler d'un retour. */
export function estUneAbsence(visitePrecedente: string | null, maintenant: number): boolean {
  const t = ms(visitePrecedente)
  return t > 0 && maintenant - t >= SEUIL_ABSENCE_JOURS * JOUR_MS
}

/** Le récap est-il encore dans sa fenêtre ? */
export function blocEnCours(bloc: BlocRetourAbsence | null, maintenant: number): bloc is BlocRetourAbsence {
  return !!bloc && maintenant < ms(bloc.jusquAu)
}

/**
 * Le bloc à garder après ce passage sur l'accueil : celui qui court encore,
 * sinon un nouveau si ce passage est un retour, sinon rien (un bloc périmé
 * s'efface).
 */
export function noterRetour(blocLu: unknown, visitePrecedente: string | null, maintenant: number): BlocRetourAbsence | null {
  const bloc = lireBlocRetour(blocLu)
  if (blocEnCours(bloc, maintenant)) return bloc
  if (!estUneAbsence(visitePrecedente, maintenant) || !visitePrecedente) return null
  return {
    version: 1,
    depuis: visitePrecedente,
    retour: new Date(maintenant).toISOString(),
    jusquAu: new Date(maintenant + DUREE_RECAP_JOURS * JOUR_MS).toISOString(),
    vu: false,
    faits: [],
    proposees: [],
  }
}

/** Cocher (ou décocher) une étape. Une valeur, pas une bascule : le bloc est relu avant d'écrire. */
export function marquerEtape(bloc: BlocRetourAbsence, id: string, fait: boolean): BlocRetourAbsence {
  const sans = bloc.faits.filter((x) => x !== id)
  return { ...bloc, faits: fait ? [...sans, id] : sans }
}

/** Retenir les étapes proposées (union), et que le récap a été vu. */
export function noterProposees(bloc: BlocRetourAbsence, ids: readonly string[], vu = bloc.vu): BlocRetourAbsence {
  const proposees = [...bloc.proposees, ...ids.filter((id) => !bloc.proposees.includes(id))]
  return { ...bloc, vu, proposees }
}

// ─── Entrée du calcul ───

export type RoleRecap = 'pilote' | 'admin' | 'operateur'

export interface Coequipier {
  nom: string
  upn: string
  role: 'titulaire' | 'suppleant'
  metiers: Metier[]
}

export interface EntreeRecap {
  bloc: BlocRetourAbsence
  maintenant: number
  role: RoleRecap
  /** Métiers et sujets pilotés ; ignoré pour l'administrateur (tout) et l'opérateur. */
  perimetre: readonly ClePerimetre[]
  moi: { nom: string; prenom: string; identites: readonly string[] }
  issues: readonly Issue[]
  /** Les événements de la période (au moins) ; les autres sont écartés. */
  evenements: readonly Commentaire[]
  /** Mes déclarations. */
  mesIds: ReadonlySet<string>
  /** Problèmes suivis (cloche, équipement, projet : utils/abonnements). */
  suivis: readonly Issue[]
  /** Les autres pilotes de mes métiers. */
  coequipiers: readonly Coequipier[]
  /** Null : pas d'accès aux FNC-T, ou pas encore lues. */
  actionsFnct: MesActions | null
  /** Qui voit les nouveautés « fnct ». */
  accesFnct: boolean
  nouveautes: readonly Nouveaute[]
}

// ─── Sortie ───

export interface CaseCalendrier {
  /** null : case vide avant le premier jour (grille alignée sur le lundi). */
  jour: Jour | null
  lettre: string
  numero: string
  weekend: boolean
}

export interface Periode {
  debut: string
  fin: string
  /** Jours pleins d'absence (entre la visite précédente et le retour). */
  jours: number
  /** « vendredi 9 octobre ». */
  derniereVisite: string
  /** « Du lundi 12 au dimanche 25 octobre ». */
  libelle: string
  /** Vide au-delà de MAX_JOURS_CALENDRIER. */
  calendrier: CaseCalendrier[]
}

export interface JourDeclarations {
  jour: Jour
  lettre: string
  parMetier: { metier: Metier; n: number }[]
  total: number
  /** Les problèmes déclarés ce jour-là (infobulle de la barre). */
  issues: Issue[]
}

export interface CarteDeclarations {
  total: number
  metiers: { metier: Metier; n: number; issues: Issue[] }[]
  parJour: JourDeclarations[]
  /** Moyenne sur une période de même durée (12 semaines avant) ; null si trop peu d'historique. */
  moyenne: number | null
  habitude: 'plus' | 'moins' | 'pareil' | null
  jourMax: { libelle: string; n: number } | null
  equipementMax: { numeroSerie: string; n: number; issues: Issue[] } | null
}

export interface Mot {
  problemId: string
  auteur: string
  auteurUpn?: string
  texte: string
  date: string
}

export interface PersonneRelais {
  nom: string
  upn?: string
  qualifies: number
  clos: number
  envois: number
  /** « Suppléance Élec et Autom » quand c'est un autre pilote de mes métiers. */
  role: string | null
  coequipier: boolean
}

export interface CarteRelais {
  personnes: PersonneRelais[]
  qualifies: number
  clos: number
  badges: { badge: BadgeCloture; n: number }[]
  envois: number
  mot: Mot | null
}

export interface CarteVosDeclarations {
  /** Problèmes à moi qui ont bougé. */
  problemes: number
  qualifiees: number
  closes: number
  annulees: number
  commentaires: number
  /** Les plus récents d'abord, un par problème. */
  lignes: { evenement: Commentaire; issue: Issue }[]
  mot: Mot | null
}

export type Urgent = { genre: 'probleme'; issue: Issue } | { genre: 'fnct'; action: MonAction }

export interface CarteAttente {
  aQualifier: number
  aQualifierVieux: number
  /** À qualifier déclarés pendant l'absence. */
  arrives: number
  critiques: number
  actionsRetard: number
  /** Opérateur : ses déclarations encore actives. */
  enCours: number
  urgents: Urgent[]
}

export interface CarteSuivis {
  /** Événements des autres sur mes problèmes suivis, pendant l'absence. */
  sonneries: number
  lignes: { issue: Issue; n: number }[]
}

export type CibleEtape =
  | { genre: 'fiche'; chemin: string }
  | { genre: 'page'; chemin: string }
  | { genre: 'accueil' }

export interface Etape {
  id: string
  titre: string
  detail: string
  fait: boolean
  /** Le libellé du bouton de la dernière carte (« Qualifier les 5 problèmes »). */
  action: string
  cible: CibleEtape
}

export interface Recap {
  role: RoleRecap
  periode: Periode
  /** « En 14 jours, l'Élec et l'Autom ont bien vécu sans vous. » */
  phrase: string
  /** « sur l'Élec et l'Autom ». */
  surPerimetre: string
  declarations: CarteDeclarations | null
  relais: CarteRelais | null
  vosDeclarations: CarteVosDeclarations | null
  attente: CarteAttente | null
  suivis: CarteSuivis | null
  nouveautes: Nouveaute[]
  etapes: Etape[]
  /** Toutes les étapes faites : le récap n'est plus proposé. */
  termine: boolean
}

// ─── Mots ───

const ARTICLE_METIER: Record<Metier, string> = {
  ELEC: 'l\'Élec',
  AUTOM: 'l\'Autom',
  MECA: 'la Méca',
  PROCESS: 'le Process',
}
const COURT_METIER: Record<Metier, string> = { ELEC: 'Élec', AUTOM: 'Autom', MECA: 'Méca', PROCESS: 'Process' }

export function libelleMetierRecap(m: Metier): string {
  return COURT_METIER[m]
}

function enumeration(mots: readonly string[]): string {
  if (mots.length <= 1) return mots[0] ?? ''
  return `${mots.slice(0, -1).join(', ')} et ${mots[mots.length - 1]}`
}

const pluriel = (n: number, un: string, plusieurs = `${un}s`): string => `${n} ${n > 1 ? plusieurs : un}`

const FORMAT_JOUR_LONG = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric', month: 'long' })
const FORMAT_JOUR_SEUL = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric' })

/** Midi UTC d'un jour : son nom ne dépend d'aucun fuseau. */
const midi = (j: Jour): Date => new Date(`${j}T12:00:00Z`)

function jourLong(j: Jour): string {
  return FORMAT_JOUR_LONG.format(midi(j))
}

/** « Du lundi 12 au dimanche 25 octobre », « Du jeudi 29 octobre au mardi 3 novembre ». */
export function libellePeriode(premier: Jour, dernier: Jour): string {
  if (premier === dernier) return `Le ${jourLong(premier)}`
  const memeMois = premier.slice(0, 7) === dernier.slice(0, 7)
  const debut = memeMois ? FORMAT_JOUR_SEUL.format(midi(premier)) : jourLong(premier)
  return `Du ${debut} au ${jourLong(dernier)}`
}

/** Le prénom est-il cité dans le texte (casse et accents ignorés, mot entier) ? */
export function citePrenom(texte: string, prenom: string): boolean {
  const p = cleNom(prenom)
  if (p.length < 2) return false
  const mots = cleNom(texte).split(/[^a-z0-9-]+/)
  return mots.includes(p)
}

function extrait(texte: string, max: number): string {
  const t = texte.replace(/\s+/g, ' ').trim()
  return t.length <= max ? t : `${t.slice(0, max - 1).trimEnd()}…`
}


// ─── Calcul ───

function periodeDe(bloc: BlocRetourAbsence): Periode {
  const premier = decalerJours(jourParis(new Date(ms(bloc.depuis))), 1)
  const dernier = decalerJours(jourParis(new Date(ms(bloc.retour))), -1)
  const jours: Jour[] = []
  for (let j = premier; j <= dernier && jours.length <= MAX_JOURS_CALENDRIER; j = decalerJours(j, 1)) jours.push(j)
  const calendrier: CaseCalendrier[] = []
  if (jours.length > 0 && jours.length <= MAX_JOURS_CALENDRIER) {
    for (let k = 0; k < rangDansSemaine(premier); k++) calendrier.push({ jour: null, lettre: INITIALES_JOURS[k] ?? '', numero: '', weekend: k >= 5 })
    for (const j of jours) {
      const r = rangDansSemaine(j)
      calendrier.push({ jour: j, lettre: INITIALES_JOURS[r] ?? '', numero: String(Number(j.slice(8, 10))), weekend: r >= 5 })
    }
  }
  return {
    debut: bloc.depuis,
    fin: bloc.retour,
    jours: Math.max(0, Math.round((midi(dernier).getTime() - midi(premier).getTime()) / JOUR_MS) + 1),
    derniereVisite: FORMAT_JOUR_LONG.format(new Date(ms(bloc.depuis))),
    libelle: jours.length ? libellePeriode(premier, dernier) : '',
    calendrier,
  }
}

function metiersDuPerimetre(e: EntreeRecap): Metier[] {
  if (e.role === 'admin') return [...METIERS_ORDRE]
  return METIERS_ORDRE.filter((m) => e.perimetre.includes(m))
}

function carteDeclarations(e: EntreeRecap, dans: (i: Issue) => boolean, metiers: readonly Metier[], debut: number, fin: number): CarteDeclarations | null {
  const perimetre = e.issues.filter(dans)
  const pendant = perimetre.filter((i) => {
    const t = ms(i.dateDeclaration)
    return t > debut && t <= fin
  })
  if (pendant.length === 0) return null
  // Un sujet sans son métier (Thibaut : Goulotte) colore avec le métier du problème.
  const couleurs = [...metiers, ...METIERS_ORDRE.filter((m) => !metiers.includes(m) && pendant.some((i) => i.metier === m))]
  const parJourMap = new Map<Jour, Issue[]>()
  for (const i of pendant) {
    const j = jourParis(new Date(ms(i.dateDeclaration)))
    parJourMap.set(j, [...(parJourMap.get(j) ?? []), i])
  }
  const premier = jourParis(new Date(debut))
  const dernier = jourParis(new Date(fin))
  const parJour: JourDeclarations[] = []
  for (let j = premier; j <= dernier && parJour.length <= MAX_JOURS_CALENDRIER; j = decalerJours(j, 1)) {
    const l = parJourMap.get(j) ?? []
    parJour.push({
      jour: j,
      lettre: INITIALES_JOURS[rangDansSemaine(j)] ?? '',
      parMetier: couleurs.map((m) => ({ metier: m, n: l.filter((i) => i.metier === m).length })).filter((x) => x.n > 0),
      total: l.length,
      issues: l,
    })
  }
  const max = parJour.reduce<JourDeclarations | null>((a, b) => (!a || b.total > a.total ? b : a), null)

  const parSerie = new Map<string, { numeroSerie: string; n: number; issues: Issue[] }>()
  for (const i of pendant) {
    const cle = i.numeroSerie.trim().toUpperCase()
    if (!cle) continue
    const x = parSerie.get(cle) ?? { numeroSerie: i.numeroSerie.trim(), n: 0, issues: [] }
    x.n += 1
    x.issues.push(i)
    parSerie.set(cle, x)
  }
  const equipementMax = [...parSerie.values()].sort((a, b) => b.n - a.n)[0] ?? null

  // D'habitude : les 12 semaines d'avant, ramenées à la durée de l'absence —
  // ou depuis la plus ancienne déclaration si l'appli est plus jeune (4 semaines au moins).
  const plusAncienne = Math.min(...perimetre.map((i) => ms(i.dateDeclaration) || Number.POSITIVE_INFINITY))
  const debutHabitude = Math.max(debut - JOURS_HABITUDE * JOUR_MS, plusAncienne)
  const etendue = debut - debutHabitude
  let moyenne: number | null = null
  let habitude: CarteDeclarations['habitude'] = null
  if (etendue >= 28 * JOUR_MS) {
    const avant = perimetre.filter((i) => {
      const t = ms(i.dateDeclaration)
      return t >= debutHabitude && t <= debut
    }).length
    moyenne = Math.round((avant * (fin - debut)) / etendue)
    if (moyenne > 0) habitude = pendant.length > moyenne * 1.15 ? 'plus' : pendant.length < moyenne * 0.85 ? 'moins' : 'pareil'
  }

  return {
    total: pendant.length,
    metiers: couleurs
      .map((m) => {
        const issues = pendant.filter((i) => i.metier === m)
        return { metier: m, n: issues.length, issues }
      })
      .filter((x) => x.n > 0),
    parJour,
    moyenne,
    habitude,
    jourMax: max && max.total >= 2 ? { libelle: jourLong(max.jour), n: max.total } : null,
    equipementMax: equipementMax && equipementMax.n >= 2 ? equipementMax : null,
  }
}

function deMoi(e: EntreeRecap, c: Commentaire): boolean {
  if (c.auteurEmail && e.moi.identites.includes(c.auteurEmail)) return true
  return memePersonne(c.auteur, e.moi.nom)
}

function coequipierDe(e: EntreeRecap, c: Commentaire): Coequipier | undefined {
  return e.coequipiers.find((p) => (c.auteurEmail && p.upn.toLowerCase() === c.auteurEmail) || memePersonne(p.nom, c.auteur))
}

function motDans(e: EntreeRecap, evenements: readonly { evenement: Commentaire }[], exigerPrenom: boolean): Mot | null {
  for (const { evenement: c } of evenements) {
    // Pas un fichier ajouté sans message : ce n'est pas un mot.
    if (c.type !== 'Commentaire' || !c.contenu.trim() || natureAutomatique(c.contenu)) continue
    if (exigerPrenom && !citePrenom(c.contenu, e.moi.prenom)) continue
    return { problemId: c.problemId, auteur: c.auteur, ...(c.auteurEmail ? { auteurUpn: c.auteurEmail } : {}), texte: c.contenu.trim(), date: c.date }
  }
  return null
}

function carteRelais(e: EntreeRecap, evts: readonly { evenement: Commentaire; issue: Issue }[]): CarteRelais | null {
  const autres = evts.filter((x) => !deMoi(e, x.evenement))
  const parPersonne = new Map<string, PersonneRelais>()
  const qualifies = new Set<string>()
  const clos = new Set<string>()
  let envois = 0
  for (const { evenement: c, issue } of autres) {
    const genre = c.type === 'Qualification' ? 'qualifies' : c.type === 'Cloture' ? 'clos' : c.type === 'Modification' && lireEvenementEnvoi(c.contenu) ? 'envois' : null
    if (!genre) continue
    if (genre === 'qualifies') qualifies.add(issue.id)
    if (genre === 'clos') clos.add(issue.id)
    if (genre === 'envois') envois += 1
    const cle = c.auteurEmail || cleNom(c.auteur)
    const co = coequipierDe(e, c)
    const upn = c.auteurEmail ?? co?.upn
    const p = parPersonne.get(cle) ?? {
      nom: co?.nom ?? c.auteur,
      ...(upn ? { upn } : {}),
      qualifies: 0,
      clos: 0,
      envois: 0,
      role: co ? `${co.role === 'suppleant' ? 'Suppléance' : 'Coordination'} ${enumeration(co.metiers.map(libelleMetierRecap))}` : null,
      coequipier: !!co,
    }
    p[genre] += 1
    parPersonne.set(cle, p)
  }
  const mot = motDans(e, autres, true)
  if (parPersonne.size === 0 && !mot) return null
  const total = (p: PersonneRelais): number => p.qualifies + p.clos + p.envois
  // Badges des problèmes clos pendant l'absence et toujours clos.
  const badges = BADGES_CLOTURE.map((b) => ({
    badge: b,
    n: e.issues.filter((i) => clos.has(i.id) && i.statut === 'Clos' && (i.badgesCloture ?? []).includes(b)).length,
  })).filter((x) => x.n > 0)
  return {
    personnes: [...parPersonne.values()].sort((a, b) => total(b) - total(a) || Number(b.coequipier) - Number(a.coequipier)),
    qualifies: qualifies.size,
    clos: clos.size,
    badges,
    envois,
    mot,
  }
}

function carteVosDeclarations(e: EntreeRecap, evts: readonly { evenement: Commentaire; issue: Issue }[]): CarteVosDeclarations | null {
  const miens = evts.filter((x) => e.mesIds.has(x.issue.id) && !deMoi(e, x.evenement) && x.evenement.type !== 'Creation')
  if (miens.length === 0) return null
  const parProbleme = new Map<string, { evenement: Commentaire; issue: Issue }>()
  for (const x of miens) if (!parProbleme.has(x.issue.id)) parProbleme.set(x.issue.id, x)
  const compte = (type: Commentaire['type']): number => new Set(miens.filter((x) => x.evenement.type === type).map((x) => x.issue.id)).size
  return {
    problemes: parProbleme.size,
    qualifiees: compte('Qualification'),
    closes: compte('Cloture'),
    annulees: compte('Annulation'),
    commentaires: miens.filter((x) => x.evenement.type === 'Commentaire').length,
    lignes: [...parProbleme.values()].slice(0, 4),
    mot: motDans(e, miens, false),
  }
}

function carteAttente(e: EntreeRecap, debut: number): CarteAttente | null {
  const actionsRetard = e.actionsFnct?.retard.length ?? 0
  const fnctUrgents: Urgent[] = (e.actionsFnct?.retard ?? []).slice(0, 1).map((action) => ({ genre: 'fnct', action }))
  if (e.role === 'operateur') {
    const miens = e.issues.filter((i) => e.mesIds.has(i.id) && estActif(i))
    const aQualifier = miens.filter((i) => i.statut === 'Ouvert').length
    if (miens.length === 0 && actionsRetard === 0) return null
    return { aQualifier, aQualifierVieux: 0, arrives: 0, critiques: 0, actionsRetard, enCours: miens.length, urgents: fnctUrgents }
  }
  const files = filesCoordinateur(e.issues, e.role === 'admin' ? [] : e.perimetre, new Map(), e.maintenant)
  const aQualifier = files.find((f) => f.id === 'aQualifier')?.issues ?? []
  const critiques = files.find((f) => f.id === 'critiques')?.issues ?? []
  if (aQualifier.length === 0 && critiques.length === 0 && actionsRetard === 0) return null
  const vieux = aQualifier.filter((i) => e.maintenant - ms(i.dateDeclaration) >= JOURS_SANS_ACTIVITE * JOUR_MS)
  const urgents: Urgent[] = [
    ...critiques.slice(0, 2).map((issue): Urgent => ({ genre: 'probleme', issue })),
    // La plus ancienne attente, si elle n'est pas déjà une critique.
    ...aQualifier.filter((i) => !critiques.includes(i)).slice(0, 1).map((issue): Urgent => ({ genre: 'probleme', issue })),
    ...fnctUrgents,
  ]
  return {
    aQualifier: aQualifier.length,
    aQualifierVieux: vieux.length,
    arrives: aQualifier.filter((i) => ms(i.dateDeclaration) > debut).length,
    critiques: critiques.length,
    actionsRetard,
    enCours: 0,
    urgents,
  }
}

function carteSuivis(e: EntreeRecap, evts: readonly { evenement: Commentaire; issue: Issue }[]): CarteSuivis | null {
  const ids = new Set(e.suivis.map((i) => i.id))
  const parProbleme = new Map<string, { issue: Issue; n: number }>()
  let sonneries = 0
  for (const x of evts) {
    if (!ids.has(x.issue.id) || deMoi(e, x.evenement)) continue
    sonneries += 1
    const l = parProbleme.get(x.issue.id) ?? { issue: x.issue, n: 0 }
    l.n += 1
    parProbleme.set(x.issue.id, l)
  }
  if (sonneries === 0) return null
  return { sonneries, lignes: [...parProbleme.values()].sort((a, b) => b.n - a.n).slice(0, 4) }
}

function nouveautesPour(e: EntreeRecap, debut: number, fin: number): Nouveaute[] {
  const premier = jourParis(new Date(debut))
  const dernier = jourParis(new Date(fin))
  const voit = (p: PublicNouveaute): boolean => p === 'tous' || (p === 'pilotes' && e.role !== 'operateur') || (p === 'fnct' && e.accesFnct)
  return e.nouveautes
    .filter((n) => n.le > premier && n.le <= dernier && voit(n.pour))
    .sort((a, b) => b.le.localeCompare(a.le))
    .slice(0, MAX_NOUVEAUTES)
}

function etapesDe(
  e: EntreeRecap,
  relais: CarteRelais | null,
  vos: CarteVosDeclarations | null,
  attente: CarteAttente | null,
  suivis: CarteSuivis | null,
): Etape[] {
  const fait = (id: string): boolean => e.bloc.faits.includes(id)
  const etapes: Etape[] = []
  const proposer = (x: Omit<Etape, 'fait'>): void => {
    etapes.push({ ...x, fait: fait(x.id) })
  }
  /** Réglée depuis qu'elle a été proposée : elle reste, cochée. */
  const reglee = (id: string, titre: string, detail: string): void => {
    if (e.bloc.proposees.includes(id) || fait(id)) etapes.push({ id, titre, detail, fait: true, action: titre, cible: { genre: 'accueil' } })
  }

  const mot = relais?.mot ?? vos?.mot ?? null
  if (mot) {
    const qui = prenom(mot.auteur, mot.auteurUpn) || mot.auteur
    proposer({
      id: 'mot',
      titre: `Lire le mot de ${qui}`,
      detail: `${mot.problemId} · « ${extrait(mot.texte, 70)} »`,
      action: `Lire le mot de ${qui}`,
      cible: { genre: 'fiche', chemin: lienProbleme(mot.problemId) },
    })
  }

  if (e.role !== 'operateur') {
    const n = attente?.aQualifier ?? 0
    if (n > 0 && attente) {
      proposer({
        id: 'qualifier',
        titre: n === 1 ? 'Qualifier le problème en attente' : `Qualifier les ${n} problèmes en attente`,
        detail: attente.aQualifierVieux > 0
          ? `Dont ${pluriel(attente.aQualifierVieux, 'déclaré')} il y a plus de ${JOURS_SANS_ACTIVITE} jours`
          : attente.arrives > 0 ? `${pluriel(attente.arrives, 'arrivé')} pendant votre absence` : 'Déclarés juste avant votre départ',
        action: n === 1 ? 'Qualifier le problème' : `Qualifier les ${n} problèmes`,
        cible: { genre: 'accueil' },
      })
    } else reglee('qualifier', 'Qualifier les problèmes en attente', 'Plus rien à qualifier')

    const critiques = (attente?.urgents ?? []).flatMap((u) => (u.genre === 'probleme' && u.issue.criticite === 'Critique' ? [u.issue] : []))
    const nc = attente?.critiques ?? 0
    if (nc > 0 && critiques[0]) {
      const series = new Set(critiques.map((i) => i.numeroSerie.trim().toUpperCase()))
      const sur = series.size === 1 && critiques[0].numeroSerie ? `, sur ${critiques[0].numeroSerie}` : ''
      proposer({
        id: 'critiques',
        titre: nc === 1 ? 'Relire le problème critique' : `Relire les ${nc} critiques`,
        detail: `${enumeration(critiques.map((i) => i.id))}${nc > critiques.length ? ' …' : ''}${sur}`,
        action: nc === 1 ? 'Relire le critique' : `Relire les ${nc} critiques`,
        cible: { genre: 'fiche', chemin: lienProbleme(critiques[0].id) },
      })
    } else reglee('critiques', 'Relire les critiques', 'Plus aucun critique en cours')
  }

  const retard = e.actionsFnct?.retard ?? []
  if (retard[0]) {
    const a = retard[0]
    proposer({
      id: 'fnct',
      titre: retard.length === 1 ? 'Clore ou reporter votre action FNC-T' : `Clore ou reporter vos ${retard.length} actions FNC-T en retard`,
      detail: `${a.fnct.numero} · ${pluriel(a.retard, 'jour')} de retard`,
      action: retard.length === 1 ? 'Voir votre action FNC-T' : 'Voir vos actions FNC-T',
      cible: retard.length === 1 ? { genre: 'fiche', chemin: lienFnct(a.fnct.numero) } : { genre: 'page', chemin: '/fnct?vue=mes' },
    })
  } else if (e.actionsFnct) reglee('fnct', 'Clore ou reporter vos actions FNC-T', 'Plus aucune action en retard')

  if (vos && vos.lignes[0]) {
    proposer({
      id: 'declarations',
      titre: 'Voir ce qui a bougé sur vos déclarations',
      detail: pluriel(vos.problemes, 'problème concerné', 'problèmes concernés'),
      action: 'Voir vos déclarations',
      cible: { genre: 'fiche', chemin: lienProbleme(vos.lignes[0].issue.id) },
    })
  }

  if (suivis && suivis.lignes[0]) {
    proposer({
      id: 'suivis',
      titre: 'Passer sur vos éléments suivis',
      detail: `${pluriel(suivis.sonneries, 'nouveauté')} sur ${pluriel(suivis.lignes.length, 'problème')}`,
      action: 'Voir vos éléments suivis',
      cible: { genre: 'accueil' },
    })
  }

  // Les étapes à faire d'abord dans l'ordre, puis celles qui sont faites.
  return etapes.slice(0, MAX_ETAPES)
}

// ─── Infobulles de la carte des déclarations (bureau, au survol) ───

const MAX_LIGNES_INFOBULLE = 5

/** « P2026-0191 · Alarme pression haute… », puis « … et 3 autres ». */
function lignesProblemes(issues: readonly Issue[]): string[] {
  const l = issues.slice(0, MAX_LIGNES_INFOBULLE).map((i) => `${i.id} · ${i.titre}`)
  const reste = issues.length - MAX_LIGNES_INFOBULLE
  if (reste > 0) l.push(`… et ${reste} ${reste > 1 ? 'autres' : 'autre'}`)
  return l
}

/** Où en sont ces problèmes aujourd'hui : « 2 critiques · 3 à qualifier · 4 clos ». */
export function etatDesProblemes(issues: readonly Issue[]): string {
  const critiques = issues.filter((i) => estActif(i) && i.criticite === 'Critique').length
  const aQualifier = issues.filter((i) => i.statut === 'Ouvert').length
  const enCours = issues.filter((i) => i.statut === 'En cours').length
  const clos = issues.filter((i) => i.statut === 'Clos').length
  const annules = issues.filter((i) => i.statut === 'Annulé').length
  return [
    critiques ? `${critiques} ${critiques > 1 ? 'critiques' : 'critique'}` : '',
    aQualifier ? `${aQualifier} à qualifier` : '',
    enCours ? `${enCours} en cours` : '',
    clos ? `${clos} clos` : '',
    annules ? `${annules} ${annules > 1 ? 'annulés' : 'annulé'}` : '',
  ].filter(Boolean).join(' · ')
}

export interface TexteInfobulle {
  titre: string
  detail?: string
  lignes: string[]
}

/** Une barre : le jour, le nombre, par métier, puis les problèmes. */
export function infobulleJour(j: JourDeclarations): TexteInfobulle {
  const jour = jourLong(j.jour)
  const titre = `${jour.charAt(0).toUpperCase()}${jour.slice(1)} · ${j.total ? `${j.total} ${j.total > 1 ? 'déclarations' : 'déclaration'}` : 'aucune déclaration'}`
  const parMetier = j.parMetier.map((x) => `${COURT_METIER[x.metier]} ${x.n}`).join(' · ')
  return { titre, ...(parMetier ? { detail: parMetier } : {}), lignes: lignesProblemes(j.issues) }
}

/** Une pastille de la légende : le métier, ce que sont devenus ses problèmes. */
export function infobulleMetier(x: { metier: Metier; n: number; issues: readonly Issue[] }): TexteInfobulle {
  const etat = etatDesProblemes(x.issues)
  return { titre: `${COURT_METIER[x.metier]} · ${x.n} ${x.n > 1 ? 'déclarations' : 'déclaration'}`, ...(etat ? { detail: `Aujourd'hui : ${etat}` } : {}), lignes: [] }
}

/** L'équipement le plus cité : ses problèmes. */
export function infobulleEquipement(x: { numeroSerie: string; issues: readonly Issue[] }): TexteInfobulle {
  const etat = etatDesProblemes(x.issues)
  return { titre: `${x.numeroSerie} · ${x.issues.length} problèmes`, ...(etat ? { detail: `Aujourd'hui : ${etat}` } : {}), lignes: lignesProblemes(x.issues) }
}

// ─── Revenir au récap après une fiche (28/10) ───

/** Au-delà, revenir à l'accueil ne rouvre plus le récap : on est passé à autre chose. */
export const REPRISE_MAX_MS = 15 * 60 * 1000

/**
 * La carte où rouvrir le récap, ou null.
 *  - `fiche-fermee` : la fiche ouverte depuis CET accueil (resté dessous, bureau) se referme ;
 *  - `accueil-remonte` : un accueil neuf (téléphone, retour d'une page) trouve la reprise
 *    notée par le précédent, si elle est récente.
 */
export function carteAReprendre(
  reprise: { carte: number; par: number; t: number } | null,
  accueil: number,
  quand: 'fiche-fermee' | 'accueil-remonte',
  maintenant: number,
): number | null {
  if (!reprise) return null
  if (quand === 'fiche-fermee') return reprise.par === accueil ? reprise.carte : null
  if (reprise.par === accueil || maintenant - reprise.t > REPRISE_MAX_MS) return null
  return reprise.carte
}

/** Rien à raconter au-delà de « Bon retour » : le récap n'est pas proposé. */
export function recapVide(r: Recap): boolean {
  return !r.declarations && !r.relais && !r.vosDeclarations && !r.attente && !r.suivis && r.nouveautes.length === 0 && r.etapes.length === 0
}

/** Tout le récap. */
export function recapAbsence(e: EntreeRecap): Recap {
  const periode = periodeDe(e.bloc)
  const debut = ms(e.bloc.depuis)
  const fin = ms(e.bloc.retour)
  const metiers = metiersDuPerimetre(e)
  const pilote = e.role !== 'operateur'
  const dans = (i: Issue): boolean => (e.role === 'admin' ? true : e.role === 'pilote' ? dansPerimetre(i, e.perimetre) : e.mesIds.has(i.id))

  const parId = new Map(e.issues.map((i) => [i.id, i]))
  const evts = e.evenements
    .filter((c) => {
      const t = ms(c.date)
      return t > debut && t <= fin
    })
    .flatMap((c) => {
      const issue = parId.get(c.problemId)
      return issue ? [{ evenement: c, issue }] : []
    })
    .sort((a, b) => ms(b.evenement.date) - ms(a.evenement.date))

  const declarations = pilote ? carteDeclarations(e, dans, metiers, debut, fin) : null
  const relais = pilote ? carteRelais(e, evts.filter((x) => dans(x.issue))) : null
  const vosDeclarations = pilote ? null : carteVosDeclarations(e, evts)
  const attente = carteAttente(e, debut)
  const suivis = carteSuivis(e, evts)
  const etapes = etapesDe(e, relais, vosDeclarations, attente, suivis)

  const surPerimetre = e.role === 'admin'
    ? 'sur les quatre métiers'
    : pilote ? `sur ${enumeration(metiers.map((m) => ARTICLE_METIER[m]))}` : ''
  const enJours = periode.jours > 0 ? `En ${pluriel(periode.jours, 'jour')}, ` : ''
  const phrase = e.role === 'admin'
    ? `${enJours}les quatre métiers ont bien vécu sans vous.`
    : pilote && metiers.length > 0
      ? `${enJours}${enumeration(metiers.map((m) => ARTICLE_METIER[m]))} ${metiers.length > 1 ? 'ont' : 'a'} bien vécu sans vous.`
      : `${enJours}l'atelier a continué sans vous.`

  return {
    role: e.role,
    periode,
    phrase: phrase.charAt(0).toUpperCase() + phrase.slice(1),
    surPerimetre,
    declarations,
    relais,
    vosDeclarations,
    attente,
    suivis,
    nouveautes: nouveautesPour(e, debut, fin),
    etapes,
    termine: etapes.length > 0 && etapes.every((x) => x.fait),
  }
}
